/**
 * useProductSearch - Shared hook for product search with autocomplete in modals
 */
import { useState, useCallback, useRef } from 'react';
import type { ProdutoCliente } from '../services/clienteService';
import clienteService from '../services/clienteService';

interface UseProductSearchOptions {
  clienteId: number;
}

export function useProductSearch({ clienteId }: UseProductSearchOptions) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ProdutoCliente[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProdutoCliente | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadAllProducts = useCallback(async (id: number) => {
    if (!id) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await clienteService.listProdutosCliente(id);
      setSearchResults(results);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const searchProducts = useCallback(async (term: string, id: number) => {
    if (!id) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    try {
      const results = await clienteService.listProdutosCliente(id, term || undefined);
      setSearchResults(results);
      setShowDropdown(results.length > 0);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setSelectedProduct(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(value, clienteId);
    }, 300);
  }, [clienteId, searchProducts]);

  const handleSelectProduct = useCallback((product: ProdutoCliente) => {
    setSelectedProduct(product);
    setSearchTerm(`${product.codigo} - ${product.descricao_completa}`);
    setShowDropdown(false);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedProduct(null);
    setShowDropdown(false);
  }, []);

  return {
    searchTerm,
    searchResults,
    isSearching,
    showDropdown,
    setShowDropdown,
    selectedProduct,
    dropdownRef,
    loadAllProducts,
    handleSearchChange,
    handleSelectProduct,
    resetSearch,
  };
}
